import React, { FC, useRef, useState } from 'react';
import styled from 'styled-components';
import Subheading from '../font/Subheading';

import Row from '../layout/Row';
import ColumnItem from '../layout/ColumnItem';
import SectionContainer from './SectionContainer';
import Spacer from '../layout/Spacer';
import RadioButtonWrapper from '../radio/RadioButtonWrapper';
import RadioButton from '../radio/RadioButton';
import RadioButtonContent from '../radio/RadioButtonContent';
import { useAppSelector, useAppDispatch } from '../../redux/redux.hooks';
import {
  changeCtaBehavior,
  changeCtaStyle,
} from '../../reducers/fit.quiz.widget.reducer';
import { P } from '../font/Parargraph';
import { TwitterPicker } from 'react-color';
import { PreviewContainer } from './PreviewContainer';
import { useClickOutside } from '../../hooks/use.click.outside';

type CtaProps = {};

const Cta: FC<CtaProps> = () => {
  const textColorRef = useRef<HTMLDivElement>(null);
  const backgroundColorRef = useRef<HTMLDivElement>(null);
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] =
    useState<boolean>(false);
  const [showFontColorPicker, setShowFontColorPicker] =
    useState<boolean>(false);
  const { data } = useAppSelector((state) => state.widgetReducer);
  const dispatch = useAppDispatch();

  useClickOutside(textColorRef, () => setShowFontColorPicker(false));
  useClickOutside(backgroundColorRef, () =>
    setShowBackgroundColorPicker(false)
  );

  const renderSettings = () => (
    <>
      <Row>
        <ColumnItem>
          <s-heading fontSize='large'>Customize the CTA button</s-heading>
          <Spacer height={8} />
          <P>
            Customize the look of the CTA buttons in the widget and also set the
            behavior of the final CTA button to either Add to Cart or Continue
            Shopping
          </P>
        </ColumnItem>
      </Row>
      <Spacer height={24} />
      <Row>
        <ColumnItem>
          <Subheading>Behavior</Subheading>
          <Spacer height={4} />
          <Info>
            This determines if clicking add to cart adds the item to cart or
            just closes the widget.
            <br />
            To change the translation, please use the translation tab
          </Info>
          <Spacer height={12} />
          <Row>
            <RadioButtonWrapper>
              <RadioButton
                groupName='behavior'
                value='cart'
                checked={data.ctaBehavior === 'cart'}
                onClick={() => {
                  dispatch(changeCtaBehavior('cart'));
                  dispatch(
                    changeCtaStyle({
                      ...data.ctaStyle,
                      text: 'Add to cart',
                    })
                  );
                }}
                content={() => (
                  <RadioButtonContent>
                    <span>Add to cart</span>
                  </RadioButtonContent>
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <RadioButton
                groupName='behavior'
                value='continue'
                checked={data.ctaBehavior === 'continue'}
                onClick={() => {
                  dispatch(changeCtaBehavior('continue'));
                  dispatch(
                    changeCtaStyle({
                      ...data.ctaStyle,
                      text: 'Continue shopping',
                    })
                  );
                }}
                content={() => (
                  <RadioButtonContent>
                    <span>Continue shopping</span>
                  </RadioButtonContent>
                )}
              />
            </RadioButtonWrapper>
          </Row>
        </ColumnItem>
      </Row>
      <Row>
        <ColumnItem>
          <s-number-field
            label='Corner radius'
            onInput={(event) =>
              dispatch(
                changeCtaStyle({
                  ...data.ctaStyle,
                  borderRadius: parseInt(event.currentTarget.value),
                })
              )
            }
            value={data.ctaStyle.borderRadius.toString()}
            autocomplete='off'
          />
        </ColumnItem>
      </Row>
      <Row>
        <ColumnItem>
          <Subheading>TEXT COLOR</Subheading>
          <Spacer height={4} />
          <Row>
            <RadioButtonWrapper>
              <RadioButton
                groupName='font-color'
                value='white'
                checked={
                  data.ctaStyle.fontColor.toLowerCase() ===
                  '#ffffff'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeCtaStyle({ ...data.ctaStyle, fontColor: '#ffffff' })
                  )
                }
                content={() => (
                  <RadioButtonContent
                    customBackground='#fff'
                    style={{ borderColor: '#e7e5e5' }}
                  />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <RadioButton
                groupName='font-color'
                value='black'
                checked={
                  data.ctaStyle.fontColor.toLowerCase() ===
                  '#000000'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeCtaStyle({ ...data.ctaStyle, fontColor: '#000000' })
                  )
                }
                content={() => (
                  <RadioButtonContent customBackground='#000000' />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <RadioButton
                groupName='font-color'
                value='blue'
                checked={
                  data.ctaStyle.fontColor.toLowerCase() ===
                  '#5673C0'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeCtaStyle({ ...data.ctaStyle, fontColor: '#5673C0' })
                  )
                }
                content={() => (
                  <RadioButtonContent customBackground='#5673C0' />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <s-button
                onClick={() => setShowFontColorPicker(!showFontColorPicker)}
              >
                Custom
              </s-button>
            </RadioButtonWrapper>
          </Row>
          {showFontColorPicker && (
            <>
              <Spacer height={12} />
              <Row ref={textColorRef}>
                <TwitterPicker
                  triangle='top-right'
                  color={data.ctaStyle.fontColor}
                  onChangeComplete={(value) =>
                    dispatch(
                      changeCtaStyle({ ...data.ctaStyle, fontColor: value.hex })
                    )
                  }
                />
              </Row>
            </>
          )}
        </ColumnItem>
        <ColumnItem>
          <Subheading>BACKGROUND COLOR</Subheading>
          <Spacer height={4} />
          <Row>
            <RadioButtonWrapper>
              <RadioButton
                groupName='background-color'
                value='black'
                checked={
                  data.ctaStyle.backgroundColor.toLowerCase() ===
                  '#0C0706'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeCtaStyle({
                      ...data.ctaStyle,
                      backgroundColor: '#0C0706',
                    })
                  )
                }
                content={() => (
                  <RadioButtonContent customBackground='#0C0706' />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <RadioButton
                groupName='background-color'
                value='white'
                checked={
                  data.ctaStyle.backgroundColor.toLowerCase() ===
                  '#FFFFFF'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeCtaStyle({
                      ...data.ctaStyle,
                      backgroundColor: '#FFFFFF',
                    })
                  )
                }
                content={() => (
                  <RadioButtonContent
                    customBackground='#FFFFFF'
                    style={{ borderColor: '#e7e5e5' }}
                  />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <RadioButton
                groupName='background-color'
                value='blue'
                checked={
                  data.ctaStyle.backgroundColor.toLowerCase() ===
                  '#5673C0'.toLowerCase()
                }
                onClick={() =>
                  dispatch(
                    changeCtaStyle({
                      ...data.ctaStyle,
                      backgroundColor: '#5673C0',
                    })
                  )
                }
                content={() => (
                  <RadioButtonContent customBackground='#5673C0' />
                )}
              />
            </RadioButtonWrapper>
            <RadioButtonWrapper>
              <s-button
                onClick={() =>
                  setShowBackgroundColorPicker(!showBackgroundColorPicker)
                }
              >
                Custom
              </s-button>
            </RadioButtonWrapper>
          </Row>
          {showBackgroundColorPicker && (
            <>
              <Spacer height={12} />
              <Row ref={backgroundColorRef}>
                <TwitterPicker
                  triangle='top-right'
                  color={data.ctaStyle.backgroundColor}
                  onChangeComplete={(value) =>
                    dispatch(
                      changeCtaStyle({
                        ...data.ctaStyle,
                        backgroundColor: value.hex,
                      })
                    )
                  }
                />
              </Row>
            </>
          )}
        </ColumnItem>
      </Row>
    </>
  );

  const renderPreview = () => {
    const { backgroundColor, fontColor, borderRadius } = data.ctaStyle;
    let buttonStyle = {
      backgroundColor,
      borderRadius: `${borderRadius}px`,
    };
    let textStyle = { color: fontColor };
    return (
      <PreviewContainer>
        <ButtonPreviewContainer style={buttonStyle}>
          <span style={textStyle}>Add to cart</span>
        </ButtonPreviewContainer>
      </PreviewContainer>
    );
  };

  return (
    <SectionContainer
      renderSettingsForm={renderSettings}
      renderPreview={renderPreview}
    />
  );
};

export default Cta;

const ButtonPreviewContainer = styled.div`
  width: 300px;
  height: 40px;
  font-size: 16px;
  padding: 4px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Info = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #556692;
`;
